import React from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useListStudentThreadsQuery } from '../../store/api/localApi';
import { Thread, ThreadStatus } from '../../types';
import ThreadView from '../messages/ThreadView';
import StartThreadButton from '../messages/StartThreadButton';

/**
 * /parent/:studentId/threads            — this child's conversations + the
 *                                         "Message staff" composer.
 * /parent/:studentId/threads/:threadId  — one conversation (shared ThreadView).
 *
 * One component serves both because the child frame (header, back link, the
 * composer entry) is identical either way; only the body swaps between the list
 * and the thread. That keeps the parent oriented — they are always inside one
 * child's context, which is the navigational axis of the whole /parent subtree.
 */
const ParentChildThreads: React.FC = () => {
  const { threadId } = useParams<{ threadId?: string }>();
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  // Conversations are about one child; an unresolved child has nothing to
  // scope to — bounce home, same as the Moments album does.
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  const threadPathBase = `/parent/${activeStudent.id}/threads`;
  const childName = activeStudent.firstName || activeStudent.displayName;

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <Link
          to={threadId ? threadPathBase : `/parent/${activeStudent.id}`}
          data-testid="parent-threads-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {threadId ? 'All conversations' : `Back to ${childName}`}
        </Link>

        {threadId ? (
          <ThreadView threadId={threadId} title={`About ${childName}`} />
        ) : (
          <ThreadList
            studentId={activeStudent.id}
            childName={childName}
            threadPathBase={threadPathBase}
          />
        )}
      </div>
    </div>
  );
};

const ThreadList: React.FC<{
  studentId: string;
  childName: string;
  threadPathBase: string;
}> = ({ studentId, childName, threadPathBase }) => {
  const { data, isLoading, error } = useListStudentThreadsQuery(studentId);
  const threads: Thread[] = data?.threads ?? [];

  return (
    <>
      <header className="bg-white rounded-lg shadow p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900">Message staff</h1>
            <p className="text-sm text-gray-500">
              Ask the team about {childName}. They&apos;ll reply here.
            </p>
          </div>
          <StartThreadButton studentId={studentId} threadPathBase={threadPathBase} />
        </div>
      </header>

      <section className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 sm:px-6 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Your conversations</h2>
        </div>

        {isLoading ? (
          <p
            className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center"
            data-testid="parent-threads-loading"
          >
            Loading conversations…
          </p>
        ) : error ? (
          <p
            className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center"
            data-testid="parent-threads-error"
          >
            Couldn&apos;t load your conversations. Please try again later.
          </p>
        ) : threads.length === 0 ? (
          <p
            className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center"
            data-testid="parent-threads-empty"
          >
            No conversations yet. Start one above and the team will get back to you.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {threads.map((thread) => (
              <li key={thread.id}>
                <Link
                  to={`${threadPathBase}/${thread.id}`}
                  data-testid={`thread-inbox-row-${thread.id}`}
                  className="flex items-start gap-3 px-4 py-4 sm:px-6 hover:bg-gray-50"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{thread.lastMessagePreview}</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'} ·{' '}
                      {formatDublinDateTime(thread.lastMessageAt)}
                    </p>
                  </div>
                  <span
                    data-testid={`thread-status-${thread.id}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium flex-shrink-0 ${
                      thread.status === ThreadStatus.OPEN
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-green-50 text-green-700'
                    }`}
                  >
                    {thread.status === ThreadStatus.OPEN ? (
                      <ClockIcon className="h-3 w-3" />
                    ) : (
                      <CheckCircleIcon className="h-3 w-3" />
                    )}
                    {thread.status === ThreadStatus.OPEN ? 'Waiting for a reply' : 'Answered'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

export default ParentChildThreads;
